import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-primary);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/images/pattern-bg.png');
    background-repeat: repeat;
    background-size: 200px;
    opacity: 0.03;
    pointer-events: none;
  }
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -10px;
    width: 60px;
    height: 3px;
    background: var(--gradient-accent);
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled(motion.span)`
  font-size: 1rem;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: block;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactForm = styled(motion.form)`
  display: flex;
  flex-direction: column;
  background: var(--gradient-glass);
  padding: 2.5rem;
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: var(--shadow-md);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-text);
  font-size: 0.9rem;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--color-text);
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--color-text);
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const SubmitButton = styled(motion.button)`
  display: inline-block;
  background: linear-gradient(135deg, var(--color-accent) 0%, rgba(212, 175, 55, 0.85) 100%);
  color: var(--color-primary);
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  font-family: var(--font-secondary);
  box-shadow: var(--shadow-accent);
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.85) 0%, var(--color-accent) 100%);
    z-index: -1;
    transition: opacity 0.3s ease;
    opacity: 0;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-accent-lg);
    
    &::before {
      opacity: 1;
    }
  }
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  background: var(--gradient-glass);
  padding: 2.5rem;
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: var(--shadow-md);
`;

const ContactInfoTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--color-accent);
`;

const ContactInfoText = styled.p`
  margin-bottom: 2rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
`;

const ContactInfoList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ContactInfoItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
  }
`;

const ContactIcon = styled.div`
  color: var(--color-accent);
  font-size: 1.2rem;
  margin-right: 1rem;
  margin-top: 0.2rem;
  background: rgba(212, 175, 55, 0.1);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.span`
  display: block;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const ContactValue = styled.span`
  color: var(--color-text-secondary);
  display: block;
  line-height: 1.5;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--color-accent);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--color-accent);
    color: var(--color-primary);
    transform: translateY(-3px);
  }
`;

const WhatsAppButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #25D366 0%, #128C7E 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
  
  &:hover {
    background: linear-gradient(90deg, #128C7E 0%, #25D366 100%);
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(37, 211, 102, 0.4);
  }
  
  svg {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
`;

const MapContainer = styled.div`
  height: 300px;
  margin-top: 2rem;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aqui você implementaria a lógica para enviar o formulário
    console.log(formData);
    alert('Mensagem enviada com sucesso!');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <Helmet>
        {/* Schema.org para Negócio Local - Importante para SEO Local */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ClothingStore",
              "name": "My Dream Suits",
              "image": "/images/logo.png",
              "url": "https://www.mydreammodafesta.com.br",
              "telephone": "+5527988340277",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "R. Getúlio Vargas, 217 - loja 02 - Glória",
                "addressLocality": "Vila Velha",
                "addressRegion": "ES",
                "postalCode": "29122-030",
                "addressCountry": "BR"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": -20.3420,
                "longitude": -40.2870
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "09:00",
                  "closes": "18:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Saturday"],
                  "opens": "09:00",
                  "closes": "14:00"
                }
              ],
              "priceRange": "$$"
            }
          `}
        </script>
      </Helmet>
      
      <ContactContainer>
        <SectionHeader>
          <SectionSubtitle
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            Entre em Contato
          </SectionSubtitle>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Fale Conosco
          </SectionTitle>
        </SectionHeader>
        
        <ContactContent>
          <ContactForm
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            <FormGroup>
              <FormLabel htmlFor="name">Nome</FormLabel>
              <FormInput 
                id="name"
                type="text" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                required 
                placeholder="Seu nome completo"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormInput 
                id="email"
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                placeholder="seu.email@exemplo.com"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="phone">Telefone</FormLabel>
              <FormInput 
                id="phone"
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                required 
                placeholder="(27) 99999-9999"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="subject">Assunto</FormLabel>
              <FormInput 
                id="subject"
                type="text" 
                name="subject" 
                value={formData.subject} 
                onChange={handleChange} 
                required 
                placeholder="Ex: Aluguel para casamento"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="message">Mensagem</FormLabel>
              <FormTextarea 
                id="message"
                name="message" 
                value={formData.message} 
                onChange={handleChange} 
                required 
                placeholder="Descreva sua necessidade, preferências e detalhes do evento..."
              />
            </FormGroup>
            <SubmitButton 
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Enviar Mensagem
            </SubmitButton>
          </ContactForm>
          
          <ContactInfo
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ContactInfoTitle>Informações de Contato</ContactInfoTitle>
            <ContactInfoText>
              Entre em contato conosco para agendar uma visita, tirar dúvidas ou fazer um orçamento. 
              Estamos à disposição para ajudá-lo a encontrar o <span className="highlight-text">traje perfeito</span> para sua ocasião especial.
            </ContactInfoText>
            
            <ContactInfoList>
              <ContactInfoItem>
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Endereço</ContactLabel>
                  <ContactValue>
                    R. Getúlio Vargas, 217 - loja 02 - Glória, Vila Velha - ES, 29122-030
                  </ContactValue>
                </ContactDetails>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon>
                  <FaPhone />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Telefone</ContactLabel>
                  <ContactValue>
                    <a href="tel:+5527988340277" style={{ color: 'inherit' }}>(27) 98834-0277</a>
                  </ContactValue>
                </ContactDetails>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon>
                  <FaEnvelope />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>
                    <a href="mailto:contato@mydreammodafesta.com.br" style={{ color: 'inherit' }}>contato@mydreammodafesta.com.br</a>
                  </ContactValue>
                </ContactDetails>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon>
                  <FaClock />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>Horário de Funcionamento</ContactLabel>
                  <ContactValue>
                    Segunda a Sexta-Feira: 09h às 18h<br />
                    Sábados: até às 14h
                  </ContactValue>
                </ContactDetails>
              </ContactInfoItem>
            </ContactInfoList>
            
            <SocialLinks>
              <SocialLink href="https://instagram.com/mydream_ternos" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FaInstagram />
              </SocialLink>
              {/* Adicione outros links de redes sociais aqui */}
            </SocialLinks>
            
            <WhatsAppButton 
              href="https://wa.me/5527988340277" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FaWhatsapp /> Fale pelo WhatsApp
            </WhatsAppButton>
            
            <MapContainer>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3741.1737054766366!2d-40.2870!3d-20.342!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xb83dcd4c06364b%3A0x9e3edeaec1cef2cc!2sR.%20Get%C3%BAlio%20Vargas%2C%20217%20-%20Gl%C3%B3ria%2C%20Vila%20Velha%20-%20ES%2C%2029122-030!5e0!3m2!1spt-BR!2sbr!4v1623418765253!5m2!1spt-BR!2sbr" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={false} 
                loading="lazy" 
                title="Localização My Dream Suits"
                aria-label="Mapa com a localização da loja My Dream Suits"
              />
            </MapContainer>
          </ContactInfo>
        </ContactContent>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact; 